import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import notificationService from '../services/notificationService';
import { useToast } from '../hooks/use-toast';
import {
  Bell,
  BellOff,
  BellRing,
  Calendar,
  Clock,
  Video,
  Check,
  X,
  Settings,
  Loader2,
  ExternalLink
} from 'lucide-react';

const NotificationCenter = ({ webinars = [], onClose }) => {
  const [permission, setPermission] = useState('default');
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    updateStatus();
  }, []);

  const updateStatus = () => {
    setPermission(notificationService.getPermissionStatus());
    // Get scheduled notifications info
    const scheduled = [];
    notificationService.scheduledNotifications.forEach((value, key) => {
      scheduled.push({
        id: key,
        ...value
      });
    });
    setScheduledNotifications(scheduled);
  };

  const handleEnableAll = async () => {
    setLoading(true);
    try {
      const granted = await notificationService.requestPermission();
      if (granted) {
        // Schedule notifications for all webinars
        let totalScheduled = 0;
        webinars.forEach(webinar => {
          const url = `${window.location.origin}/webinars/${webinar.id}/sala`;
          const scheduled = notificationService.scheduleWebinarReminders(webinar, url);
          totalScheduled += scheduled.length;
          localStorage.setItem(`notification-webinar-${webinar.id}`, 'enabled');
        });
        
        toast({
          title: "🔔 Notificaciones activadas",
          description: `Se programaron ${totalScheduled} recordatorios para tus webinars`,
        });
        updateStatus();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron activar las notificaciones",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const handleDisableAll = () => {
    notificationService.cancelAllScheduledNotifications();
    webinars.forEach(webinar => {
      localStorage.removeItem(`notification-webinar-${webinar.id}`);
    });
    updateStatus();
    toast({
      title: "Notificaciones desactivadas",
      description: "Se cancelaron todos los recordatorios programados",
    });
  };

  const formatScheduledTime = (scheduledTime) => {
    const date = new Date(scheduledTime);
    return date.toLocaleString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (permission === 'unsupported') {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <BellOff className="h-5 w-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Tu navegador no soporta notificaciones push
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            Centro de Notificaciones
          </CardTitle>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Permission Status */}
        <div className={`p-4 rounded-lg ${
          permission === 'granted' ? 'bg-green-50 border border-green-200' :
          permission === 'denied' ? 'bg-red-50 border border-red-200' :
          'bg-gray-50 border border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {permission === 'granted' ? (
                <BellRing className="h-5 w-5 text-green-600" />
              ) : permission === 'denied' ? (
                <BellOff className="h-5 w-5 text-red-600" />
              ) : (
                <Bell className="h-5 w-5 text-gray-600" />
              )}
              <div>
                <p className="font-medium text-sm">
                  {permission === 'granted' ? 'Notificaciones activas' :
                   permission === 'denied' ? 'Notificaciones bloqueadas' :
                   'Notificaciones no configuradas'}
                </p>
                <p className="text-xs text-gray-500">
                  {permission === 'granted' 
                    ? `${scheduledNotifications.length} recordatorios programados` 
                    : permission === 'denied'
                    ? 'Actívalas en la configuración del navegador'
                    : 'Activa las notificaciones para recibir recordatorios'}
                </p>
              </div>
            </div>
            
            {permission === 'granted' ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDisableAll}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Desactivar
              </Button>
            ) : permission !== 'denied' && (
              <Button 
                size="sm"
                onClick={handleEnableAll}
                disabled={loading}
                className="bg-gray-900 hover:bg-gray-800"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Activar'
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Scheduled Notifications List */}
        {scheduledNotifications.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Recordatorios programados:</h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {scheduledNotifications.map(notif => (
                <div 
                  key={notif.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center space-x-2">
                    <Video className="h-4 w-4 text-gray-400" />
                    <span className="font-medium truncate max-w-[200px]">
                      {notif.title}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {formatScheduledTime(notif.scheduledTime)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Webinars */}
        {webinars.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Tus próximos webinars:</h4>
            <div className="space-y-2">
              {webinars.slice(0, 3).map(webinar => {
                const isEnabled = localStorage.getItem(`notification-webinar-${webinar.id}`) === 'enabled';
                return (
                  <div 
                    key={webinar.id}
                    className="flex items-center justify-between p-3 bg-white border rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{webinar.title}</p>
                      <p className="text-xs text-gray-500 flex items-center mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(webinar.date).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                      {isEnabled ? (
                        <><BellRing className="h-3 w-3 mr-1" /> Activo</>
                      ) : (
                        <><Bell className="h-3 w-3 mr-1" /> Inactivo</>
                      )}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center pt-2">
          Los recordatorios se envían: 1 día, 1 hora y 15 minutos antes del webinar
        </p>
      </CardContent>
    </Card>
  );
};

export default NotificationCenter;
